import type { ComponentType } from 'react';
import type { DetailFormApi } from '../detail-form/types';
import type { ColumnMetadata } from '../grid/types';
import { CurrencyRateJsMethodBinding } from './CurrencyRateJsMethodBinding';
import { EmployeeReviewFeedbackContextJsMethodBinding } from './EmployeeReviewFeedbackContextJsMethodBinding';
import { EmployeeReviewPeriodJsMethodBinding } from './EmployeeReviewPeriodJsMethodBinding';
import { InvoiceBankJsMethodBinding } from './InvoiceBankJsMethodBinding';
import { EmployeePhotoJsMethodBinding } from './EmployeePhotoJsMethodBinding';
import { SelfProfilePhotoJsMethodBinding } from './SelfProfilePhotoJsMethodBinding';
import { INVOICE_BANK_BY_CURRENCY_JS_METHOD } from './invoiceBankOptions';

export { INVOICE_BANK_BY_CURRENCY_JS_METHOD } from './invoiceBankOptions';

export const CURRENCY_RATE_JS_METHOD = 'ZAssets.Fields.MethodUpdatingCurrencyRateInField';
export const EMPLOYEE_REVIEW_CONTEXT_JS_METHOD =
  'ZAssets.Fields.MethodShowEmployeeReviewFeedbackContext';
export const EMPLOYEE_REVIEW_PERIOD_JS_METHOD = 'ZAssets.Fields.MethodSetEmployeeReviewPeriod';
export const EMPLOYEE_PHOTO_JS_METHOD = 'ZAssets.ProfilePhoto.MethodRenderEmployeeDetails';
export const SELF_PROFILE_PHOTO_JS_METHOD = 'ZAssets.ProfilePhoto.MethodRender';

export type JsMethodHost = 'grid' | 'detailForm';

export type JsMethodNotice = {
  message: string;
  severity: 'success' | 'error' | 'warning';
};

export type DetailFormJsMethodProps = {
  field: ColumnMetadata;
  fields: ColumnMetadata[];
  form: DetailFormApi;
  contentKey?: string;
  parentId?: string;
  onNotify: (notice: JsMethodNotice) => void;
};

type JsMethodConfig = {
  grid?: true;
  detailFormHandler?: ComponentType<DetailFormJsMethodProps>;
  renderBeforeField?: true;
  renderOnce?: true;
};

const jsMethods: Record<string, JsMethodConfig> = {
  [CURRENCY_RATE_JS_METHOD]: { detailFormHandler: CurrencyRateJsMethodBinding },
  [EMPLOYEE_PHOTO_JS_METHOD]: {
    detailFormHandler: EmployeePhotoJsMethodBinding,
    renderBeforeField: true,
  },
  [SELF_PROFILE_PHOTO_JS_METHOD]: {
    detailFormHandler: SelfProfilePhotoJsMethodBinding,
    renderBeforeField: true,
  },
  [EMPLOYEE_REVIEW_PERIOD_JS_METHOD]: { detailFormHandler: EmployeeReviewPeriodJsMethodBinding },
  [INVOICE_BANK_BY_CURRENCY_JS_METHOD]: { detailFormHandler: InvoiceBankJsMethodBinding },
  [EMPLOYEE_REVIEW_CONTEXT_JS_METHOD]: {
    grid: true,
    detailFormHandler: EmployeeReviewFeedbackContextJsMethodBinding,
    renderBeforeField: true,
  },
};

const getJsMethod = (name: string | null | undefined): JsMethodConfig | undefined =>
  name ? jsMethods[name.trim()] : undefined;

export const getDetailFormJsMethodConfig = (name: string | null | undefined) => getJsMethod(name);

export const isJsMethodSupported = (
  name: string | null | undefined,
  host: JsMethodHost
): boolean => {
  const method = getJsMethod(name);
  return host === 'grid' ? method?.grid === true : method?.detailFormHandler != null;
};

export const getDetailFormJsMethodHandler = (name: string | null | undefined) =>
  getJsMethod(name)?.detailFormHandler;
